import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Slider,
  Typography
} from '@mui/material';
import PropTypes from 'prop-types';
import {PLAYER_STATES, playbackStatusMessage} from 'utils/playerPlayback';

const CONTROLS_HIDE_MS = 3200;

function hasVisibleKeyboardFocus(target) {
  if (!target || typeof target.matches !== 'function') return false;
  try {
    return target.matches(':focus-visible');
  } catch {
    return false;
  }
}

function PlayerChrome({
  children,
  containerRef,
  videoRef,
  status,
  variant,
  playingName,
  attempt,
  retryAt,
  reason,
  bufferSeconds,
  userPaused,
  online,
  ultraReserveLabel,
  ultraReserveStatus,
  ultraReserveColor,
  onRequestPlay,
  onRequestPause,
  onRetryNow,
  audioTracks = [],
  selectedAudioTrack = 'auto',
  onAudioTrackChange
}) {
  const [controlsVisible, setControlsVisible] = useState(true);
  const [keyboardFocusWithin, setKeyboardFocusWithin] = useState(false);
  const [audioFocused, setAudioFocused] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(100);
  const [clock, setClock] = useState(() => Date.now());
  const hideTimerRef = useRef(null);
  const inputModalityRef = useRef('unknown');
  const previousAudibleVolumeRef = useRef(100);

  const isTerminal = status === PLAYER_STATES.TERMINAL_ERROR;
  const isPaused = userPaused || status === PLAYER_STATES.PAUSED || isTerminal;
  // FROZEN behaves like PLAYING for the chrome: the whole point of the freeze
  // profile is a quiet picture, so controls auto-hide instead of pinning.
  const pinControls =
    audioFocused ||
    keyboardFocusWithin ||
    userPaused ||
    (status !== PLAYER_STATES.PLAYING && status !== PLAYER_STATES.FROZEN);
  const chromeVisible = controlsVisible || pinControls;
  const topChromeVisible = !isFullscreen || chromeVisible;
  const feedback = useMemo(
    () => playbackStatusMessage(status, {attempt, retryAt, reason, bufferSeconds, now: clock}),
    [attempt, bufferSeconds, clock, reason, retryAt, status]
  );
  // FROZEN keeps the last frame fully visible (discreet chip only) and
  // WAITING_SLOT is owned by the screen manager rendered as a child.
  const overlayVisible =
    status !== PLAYER_STATES.PLAYING &&
    status !== PLAYER_STATES.FROZEN &&
    status !== PLAYER_STATES.WAITING_SLOT;
  const canRetry =
    typeof onRetryNow === 'function' &&
    status !== PLAYER_STATES.PREPARING &&
    status !== PLAYER_STATES.CONNECTING &&
    status !== PLAYER_STATES.STABILIZING &&
    status !== PLAYER_STATES.RECOVERED &&
    status !== PLAYER_STATES.WAITING_SLOT;

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current) window.clearTimeout(hideTimerRef.current);
    hideTimerRef.current = null;
  }, []);

  const revealControls = useCallback(() => {
    clearHideTimer();
    setControlsVisible(true);
    if (!pinControls) {
      hideTimerRef.current = window.setTimeout(() => setControlsVisible(false), CONTROLS_HIDE_MS);
    }
  }, [clearHideTimer, pinControls]);

  useEffect(() => {
    revealControls();
    return clearHideTimer;
  }, [clearHideTimer, revealControls]);

  useEffect(() => {
    const rememberKeyboard = () => {
      inputModalityRef.current = 'keyboard';
    };
    const rememberPointer = () => {
      inputModalityRef.current = 'pointer';
    };

    document.addEventListener('keydown', rememberKeyboard, true);
    document.addEventListener('pointerdown', rememberPointer, true);
    return () => {
      document.removeEventListener('keydown', rememberKeyboard, true);
      document.removeEventListener('pointerdown', rememberPointer, true);
    };
  }, []);

  useEffect(() => {
    if (status !== PLAYER_STATES.WAITING_RETRY || !retryAt) return undefined;
    setClock(Date.now());
    const timer = window.setInterval(() => setClock(Date.now()), 250);
    return () => window.clearInterval(timer);
  }, [retryAt, status]);

  useEffect(() => {
    const onFullscreenChange = () => {
      const active = document.fullscreenElement || document.webkitFullscreenElement;
      setIsFullscreen(active === containerRef.current);
      revealControls();
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', onFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', onFullscreenChange);
    };
  }, [containerRef, revealControls]);

  useEffect(() => {
    if (!keyboardFocusWithin) return;
    const container = containerRef.current;
    if (!container || !container.contains(document.activeElement)) {
      setKeyboardFocusWithin(false);
    }
  }, [containerRef, keyboardFocusWithin, status]);

  const handlePlayPause = useCallback(() => {
    if (isTerminal) return;
    if (isPaused) onRequestPlay();
    else onRequestPause();
    revealControls();
  }, [isPaused, isTerminal, onRequestPause, onRequestPlay, revealControls]);

  const handleMuteToggle = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    const isSilent = video.muted || video.volume === 0;
    if (isSilent) {
      video.muted = false;
      if (video.volume === 0) {
        const restored = Math.max(1, previousAudibleVolumeRef.current || 50);
        video.volume = restored / 100;
        setVolume(restored);
      }
    } else {
      video.muted = true;
    }
    setMuted(video.muted);
    revealControls();
  }, [revealControls, videoRef]);

  const setVideoVolume = useCallback(
    (nextVolume) => {
      const video = videoRef.current;
      if (!video) return;
      const safeVolume = Math.max(0, Math.min(100, nextVolume));
      video.volume = safeVolume / 100;
      if (safeVolume > 0) {
        previousAudibleVolumeRef.current = safeVolume;
        if (video.muted) video.muted = false;
      }
      setMuted(video.muted);
      setVolume(safeVolume);
      revealControls();
    },
    [revealControls, videoRef]
  );

  const handleVolumeChange = useCallback(
    (_event, value) => setVideoVolume(Array.isArray(value) ? value[0] : value),
    [setVideoVolume]
  );

  const handleFullscreenToggle = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;
    revealControls();
    const active = document.fullscreenElement || document.webkitFullscreenElement;
    if (active) {
      const exit = document.exitFullscreen || document.webkitExitFullscreen;
      if (exit) Promise.resolve(exit.call(document)).catch(() => {});
    } else {
      const request = container.requestFullscreen || container.webkitRequestFullscreen;
      if (request) Promise.resolve(request.call(container)).catch(() => {});
    }
  }, [containerRef, revealControls]);

  const handlePointerActivity = useCallback(() => {
    inputModalityRef.current = 'pointer';
    setKeyboardFocusWithin(false);
    revealControls();
  }, [revealControls]);

  const handleKeyboardActivity = useCallback(() => {
    inputModalityRef.current = 'keyboard';
    clearHideTimer();
    setControlsVisible(true);
    setKeyboardFocusWithin(true);
  }, [clearHideTimer]);

  const handleKeyDown = useCallback(
    (event) => {
      const tagName = String(event.target?.tagName || '').toLowerCase();
      if (
        tagName === 'button' ||
        tagName === 'input' ||
        tagName === 'select' ||
        tagName === 'option' ||
        event.target?.getAttribute?.('role') === 'slider'
      ) {
        return;
      }
      const key = event.key.toLowerCase();
      if (key === ' ' || key === 'k' || key === 'mediaplaypause') {
        event.preventDefault();
        handlePlayPause();
      } else if (key === 'm') {
        event.preventDefault();
        handleMuteToggle();
      } else if (key === 'f') {
        event.preventDefault();
        handleFullscreenToggle();
      } else if (key === 'arrowup' || key === 'arrowdown') {
        event.preventDefault();
        setVideoVolume(volume + (key === 'arrowup' ? 5 : -5));
      }
    },
    [handleFullscreenToggle, handleMuteToggle, handlePlayPause, setVideoVolume, volume]
  );

  const handleFocusCapture = useCallback(
    (event) => {
      const keyboardDriven =
        inputModalityRef.current === 'keyboard' ||
        (inputModalityRef.current === 'unknown' && hasVisibleKeyboardFocus(event.target));
      setKeyboardFocusWithin(keyboardDriven);
      revealControls();
    },
    [revealControls]
  );

  const handleBlurCapture = useCallback((event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) setKeyboardFocusWithin(false);
  }, []);

  const fullscreenVariantLabel = variant === 'C' ? 'Ultra C' : `Player ${variant}`;
  const hasReserveChrome = variant === 'C' || variant === 'D';
  const showSpinner = ![
    PLAYER_STATES.PAUSED,
    PLAYER_STATES.RECOVERED,
    PLAYER_STATES.TERMINAL_ERROR,
    PLAYER_STATES.OFFLINE
  ].includes(status);

  return (
    <Paper
      ref={containerRef}
      component="section"
      role="region"
      aria-label={`Reproductor de ${playingName || 'canal en directo'}`}
      tabIndex={0}
      elevation={0}
      onKeyDown={handleKeyDown}
      onKeyDownCapture={handleKeyboardActivity}
      onPointerMove={handlePointerActivity}
      onPointerDown={handlePointerActivity}
      onDoubleClick={handleFullscreenToggle}
      onFocusCapture={handleFocusCapture}
      onBlurCapture={handleBlurCapture}
      sx={{
        borderRadius: isFullscreen ? 0 : 3,
        overflow: 'hidden',
        border: isFullscreen ? 0 : '1px solid',
        borderColor: 'divider',
        backgroundColor: '#000',
        aspectRatio: isFullscreen ? 'auto' : '16/9',
        maxHeight: isFullscreen ? 'none' : '68vh',
        width: '100%',
        height: isFullscreen ? '100%' : 'auto',
        position: 'relative',
        cursor: isFullscreen && !chromeVisible ? 'none' : 'default',
        outline: 'none',
        '&:focus-visible': {boxShadow: '0 0 0 3px', color: 'primary.main'}
      }}
    >
      {children}

      <Box
        sx={{
          position: 'absolute',
          top: 12,
          left: 12,
          right: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          pointerEvents: 'none',
          opacity: topChromeVisible ? 1 : 0,
          transition: 'opacity 180ms ease',
          zIndex: 3,
          '@media (prefers-reduced-motion: reduce)': {transition: 'none'}
        }}
      >
        {isFullscreen && (
          <Chip
            label={fullscreenVariantLabel}
            aria-label={`Modo de reproducción: ${fullscreenVariantLabel}`}
            color={variant === 'C' ? 'primary' : variant === 'D' ? 'info' : 'secondary'}
            size="small"
            sx={{fontWeight: 800}}
          />
        )}
        {hasReserveChrome && ultraReserveLabel && (
          <Chip
            label={`${ultraReserveLabel} · ${ultraReserveStatus}`}
            aria-label={`${ultraReserveLabel}. ${ultraReserveStatus}`}
            color={ultraReserveColor}
            size="small"
            sx={{fontWeight: 800}}
          />
        )}
        <Box sx={{flexGrow: 1}} />
        {status === PLAYER_STATES.PLAYING && (
          <Chip label="EN VIVO" color="error" size="small" sx={{fontWeight: 700}} />
        )}
      </Box>

      {status === PLAYER_STATES.FROZEN && (
        <Box
          sx={{
            // Own container: the discreet chip must stay visible through the
            // fullscreen auto-hide, replacing the "EN VIVO" position.
            position: 'absolute',
            top: 12,
            right: 12,
            pointerEvents: 'none',
            zIndex: 4
          }}
        >
          <Chip
            label="Reconectando…"
            aria-label="Señal interrumpida, reconectando"
            color="warning"
            size="small"
            sx={{fontWeight: 700}}
          />
        </Box>
      )}

      {overlayVisible && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            px: 3,
            pb: 7,
            textAlign: 'center',
            zIndex: 2,
            backgroundColor:
              status === PLAYER_STATES.RECOVERED ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.7)'
          }}
        >
          {showSpinner && <CircularProgress color="inherit" size={38} sx={{color: '#fff'}} />}
          <Typography sx={{color: '#fff', fontWeight: 800, fontSize: {xs: 17, sm: 21}}}>
            {feedback.title}
          </Typography>
          <Typography variant="body2" sx={{color: 'rgba(255,255,255,0.82)', maxWidth: 560}}>
            {feedback.detail}
          </Typography>
          {status === PLAYER_STATES.PAUSED && (
            <Button variant="contained" startIcon={<PlayArrowIcon />} onClick={onRequestPlay}>
              {reason === 'autoplayBlocked' ? 'Iniciar reproducción' : 'Continuar'}
            </Button>
          )}
          {canRetry && status !== PLAYER_STATES.PAUSED && (
            <Button
              variant="contained"
              color={status === PLAYER_STATES.TERMINAL_ERROR ? 'primary' : 'inherit'}
              startIcon={<RefreshIcon />}
              onClick={onRetryNow}
              disabled={!online}
            >
              Reintentar ahora
            </Button>
          )}
        </Box>
      )}

      <Typography
        component="span"
        aria-live="polite"
        aria-atomic="true"
        sx={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          p: 0,
          m: -1,
          overflow: 'hidden',
          clip: 'rect(0,0,0,0)',
          whiteSpace: 'nowrap',
          border: 0
        }}
      >
        {feedback.title}. {feedback.detail}
      </Typography>

      {hasReserveChrome && ultraReserveStatus && (
        <Typography
          component="span"
          aria-live="polite"
          aria-atomic="true"
          sx={{
            position: 'absolute',
            width: '1px',
            height: '1px',
            p: 0,
            m: -1,
            overflow: 'hidden',
            clip: 'rect(0,0,0,0)',
            whiteSpace: 'nowrap',
            border: 0
          }}
        >
          Estado del colchón Ultra: {ultraReserveStatus}
        </Typography>
      )}

      <Box
        role="toolbar"
        aria-label="Controles del reproductor"
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          gap: {xs: 0.25, sm: 1},
          px: {xs: 0.75, sm: 1.5},
          py: 0.75,
          background: 'linear-gradient(transparent, rgba(0,0,0,0.88))',
          opacity: chromeVisible ? 1 : 0,
          pointerEvents: chromeVisible ? 'auto' : 'none',
          transform: chromeVisible ? 'translateY(0)' : 'translateY(8px)',
          transition: 'opacity 180ms ease, transform 180ms ease',
          zIndex: 3,
          '@media (prefers-reduced-motion: reduce)': {transition: 'none'}
        }}
      >
        <IconButton
          onClick={handlePlayPause}
          sx={{color: '#fff'}}
          aria-label={
            isTerminal ? 'Reproducción no disponible' : isPaused ? 'Reproducir' : 'Pausar'
          }
          disabled={isTerminal}
        >
          {isPaused ? <PlayArrowIcon /> : <PauseIcon />}
        </IconButton>
        <IconButton
          onClick={handleMuteToggle}
          sx={{color: '#fff'}}
          aria-label={muted || volume === 0 ? 'Activar sonido' : 'Silenciar'}
        >
          {muted || volume === 0 ? <VolumeOffIcon /> : <VolumeUpIcon />}
        </IconButton>
        <Slider
          value={muted ? 0 : volume}
          onChange={handleVolumeChange}
          size="small"
          sx={{width: {xs: 64, sm: 96}, color: '#fff'}}
          aria-label="Volumen"
        />
        {audioTracks.length > 1 && (
          <Box
            component="select"
            aria-label="Pista de audio"
            title="Pista de audio"
            disabled={isTerminal}
            onFocus={() => setAudioFocused(true)}
            onBlur={() => setAudioFocused(false)}
            value={selectedAudioTrack}
            onChange={(event) => { onAudioTrackChange?.(event.target.value); revealControls() }}
            onDoubleClick={(event) => event.stopPropagation()}
            sx={{maxWidth: {xs: 110, sm: 200}, minHeight: 36, color: '#fff',
              backgroundColor: '#202020', border: '1px solid #777', borderRadius: 1}}
          >
            <option value="auto">Audio automático</option>
            {audioTracks.map(track => <option key={track.id} value={track.id}>{track.label}</option>)}
          </Box>
        )}
        <Box sx={{flexGrow: 1, minWidth: 0}}>
          <Typography
            variant="body2"
            sx={{
              color: '#fff',
              fontWeight: 600,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {playingName || 'Canal en directo'}
          </Typography>
        </Box>
        <IconButton
          onClick={handleFullscreenToggle}
          sx={{color: '#fff'}}
          aria-label={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
        >
          {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
        </IconButton>
      </Box>
    </Paper>
  );
}

PlayerChrome.propTypes = {
  children: PropTypes.node.isRequired,
  containerRef: PropTypes.shape({current: PropTypes.any}).isRequired,
  videoRef: PropTypes.shape({current: PropTypes.any}).isRequired,
  status: PropTypes.oneOf(Object.values(PLAYER_STATES)).isRequired,
  variant: PropTypes.oneOf(['A', 'B', 'C', 'D']).isRequired,
  playingName: PropTypes.string,
  attempt: PropTypes.number,
  retryAt: PropTypes.number,
  reason: PropTypes.string,
  bufferSeconds: PropTypes.number,
  userPaused: PropTypes.bool.isRequired,
  online: PropTypes.bool.isRequired,
  ultraReserveLabel: PropTypes.string,
  ultraReserveStatus: PropTypes.string,
  ultraReserveColor: PropTypes.oneOf([
    'default',
    'primary',
    'secondary',
    'error',
    'info',
    'success',
    'warning'
  ]),
  onRequestPlay: PropTypes.func.isRequired,
  onRequestPause: PropTypes.func.isRequired,
  onRetryNow: PropTypes.func,
  audioTracks: PropTypes.arrayOf(PropTypes.shape({id: PropTypes.string.isRequired, label: PropTypes.string.isRequired})),
  selectedAudioTrack: PropTypes.string,
  onAudioTrackChange: PropTypes.func
};

PlayerChrome.defaultProps = {
  attempt: 0,
  retryAt: null,
  reason: null,
  bufferSeconds: null,
  ultraReserveLabel: null,
  ultraReserveStatus: null,
  ultraReserveColor: 'default',
  onRetryNow: null
};

export default PlayerChrome;
